import { useUsersAccessMutations } from '../../hooks/useUsersAccessMutations'
import type {
  UsersAccessPendingInviteRowI,
  UsersAccessTechnicianRowI,
  UsersAccessUserRowI,
} from '../../types/usersAccess'
import { useTranslation } from 'react-i18next'

import { Alert, Modal, Stack, Text } from '@hivewatch/dark'
import type { ColorScheme } from '@hivewatch/dark'

export type ConfirmUsersAccessAction =
  | { type: 'offboard-user'; row: UsersAccessUserRowI }
  | { type: 'cancel-invitation'; row: UsersAccessPendingInviteRowI }
  | { type: 'remove-technician'; row: UsersAccessTechnicianRowI }

interface ConfirmUsersAccessActionModalIProps {
  action: ConfirmUsersAccessAction
  colorScheme: ColorScheme
  isOpen: boolean
  onClose: () => void
}

export const ConfirmUsersAccessActionModal = ({
  action,
  colorScheme,
  isOpen,
  onClose,
}: ConfirmUsersAccessActionModalIProps) => {
  const { t } = useTranslation()
  const {
    cancelInvitation,
    isOffboarding,
    isRemovingTechnician,
    offboardAccount,
    removeTechnician,
  } = useUsersAccessMutations(colorScheme)

  const copy = {
    'offboard-user': {
      title: t('settings:rebrand_users_offboard_title'),
      body: t('settings:rebrand_users_offboard_body'),
      action: t('settings:rebrand_users_offboard_action'),
    },
    'cancel-invitation': {
      title: t('settings:rebrand_users_cancel_invitation_title'),
      body: t('settings:rebrand_users_cancel_invitation_body'),
      action: t('settings:rebrand_users_cancel_invitation_action'),
    },
    'remove-technician': {
      title: t('settings:rebrand_users_remove_technician_title'),
      body: t('settings:rebrand_users_remove_technician_body'),
      action: t('settings:rebrand_users_remove_technician_action'),
    },
  }[action.type]

  const isLoading =
    action.type === 'remove-technician' ? isRemovingTechnician : isOffboarding

  const save = async () => {
    const didSave =
      action.type === 'offboard-user'
        ? await offboardAccount(action.row)
        : action.type === 'cancel-invitation'
        ? await cancelInvitation(action.row)
        : await removeTechnician(action.row)
    if (didSave) onClose()
  }

  return (
    <Modal
      closeOnEsc={!isLoading}
      closeOnOverlayClick={!isLoading}
      colorScheme={colorScheme}
      isCloseHidden={isLoading}
      isOpen={isOpen}
      isSaveDestructive
      isSaveLoading={isLoading}
      onClose={onClose}
      onSave={save}
      saveButtonLabel={copy.action}
      showCancelButton={!isLoading}
      size='compact'
      title={copy.title}
    >
      <Stack gap='16px'>
        <Text textStyle='body'>{copy.body}</Text>
        <Text textStyle='body-bold'>
          {action.row.fullName || action.row.email}
        </Text>
        <Alert
          colorScheme={colorScheme}
          description={t('settings:rebrand_users_destructive_warning')}
          variant='warning'
        />
      </Stack>
    </Modal>
  )
}
